import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, Mail, Lock, User, AlertCircle, Building2, Ticket } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    org_name: "",
    invite_code: searchParams.get("code") || ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.invite_code.trim()) {
      setError("Invite code is required");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      localStorage.setItem("token", response.data.token);
      toast.success("Account created! You are the team manager.");
      navigate("/dashboard", { state: { user: response.data.user } });
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <span 
              className="font-bold text-2xl text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Storm Pad
            </span>
          </Link>
        </div>

        <Card className="border-slate-100 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle 
              className="text-2xl font-bold text-center"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Create your team
            </CardTitle>
            <CardDescription className="text-center">
              Enter your invite code to set up your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite_code">Invite Code *</Label>
                <div className="relative">
                  <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="invite_code"
                    name="invite_code"
                    type="text"
                    placeholder="ABC-123-XYZ"
                    value={formData.invite_code}
                    onChange={handleChange}
                    className="pl-10 uppercase"
                    required
                    data-testid="invite-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org_name">Team/Company Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="org_name"
                    name="org_name"
                    type="text"
                    placeholder="Acme Sales Team"
                    value={formData.org_name}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="org-name-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Leave blank to use your name</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Your Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10"
                    minLength={6}
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="p-3 rounded-lg bg-indigo-50 text-sm text-indigo-700">
                <p className="font-medium">You'll be the team manager</p>
                <p className="text-indigo-600">Add team members after signing up via Team Management.</p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner"></div>
                    Creating team...
                  </span>
                ) : (
                  "Create Team"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-600">
              Already have an account?{" "}
              <Link 
                to="/login" 
                className="font-medium text-indigo-600 hover:text-indigo-500"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
